import 'vite/modulepreload-polyfill';
import gsap from 'gsap';
import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';
import { computed, createApp, onMounted, onUnmounted, ref } from 'vue';

import { ANIMATION_CONFIGS } from '~/constants/animations';

import { BaseSection } from './base-section';

const EDITIONS = {
  ALL: 'all',
  SALE: 'sale',
};

const EDITION_PARAM = 'edition';

/**
 * @type {import('swiper/types').SwiperOptions}
 */
const BASE_CAROUSEL_OPTIONS = {
  loop: false,
  modules: [Navigation],
  navigation: {
    nextEl: '.product-hero-next-button',
    prevEl: '.product-hero-prev-button',
  },
};

/**
 * PDP Hero Section Controller using Vue Composition API, wrapped in BaseSection.
 * Handles the image gallery carousel functionality.
 */
class ProductHeroSection extends BaseSection {
  constructor() {
    super('[data-section-type="product-hero"]');
  }

  /**
   * Initializes the section by creating and mounting the Vue app.
   * @param {Object} instanceData - Data associated with the section instance.
   */
  init(instanceData) {
    const container = instanceData.container;

    createApp({
      name: 'ProductHeroSection',
      setup() {
        // State
        const swiperInstance = ref(null);
        const currentSlideIndex = ref(1);
        const swiperIsBeginning = ref(true);
        const swiperIsEnd = ref(false);
        const selectedEdition = ref(EDITIONS.ALL);
        const colorwayName = ref('');
        const isShoes = computed(() => container.getAttribute('data-is-shoes') === 'true');
        const isFinalSaleTooltipOpen = ref(false);
        const isSaleProduct = ref(false);

        /**
         * Initializes the Swiper instance.
         */
        const initSwiper = () => {
          const swiperContainer = container.querySelector('.swiper');
          if (!swiperContainer) return;

          const shoesDesktopSwiperOptions = {
            centeredSlides: true,
            slidesPerView: 'auto',
            spaceBetween: 20,
            speed: 800,
          };

          const otherDesktopSwiperOptions = {
            centeredSlides: false,
            slidesPerView: 'auto',
            spaceBetween: 20,
            speed: 800,
          };

          const swiperOptions = {
            ...BASE_CAROUSEL_OPTIONS,
            breakpoints: {
              0: {
                centeredSlides: true,
                slidesPerView: 1.13,
                spaceBetween: 10,
                speed: 400,
              },
              768: isShoes.value ? shoesDesktopSwiperOptions : otherDesktopSwiperOptions,
            },
            on: {
              slideChange: (swiper) => {
                currentSlideIndex.value = swiper.activeIndex + 1;
                swiperIsBeginning.value = swiper.isBeginning;
                swiperIsEnd.value = swiper.isEnd;
              },
            },
          };

          swiperInstance.value = new Swiper(swiperContainer, swiperOptions);
          fadeInCarousel();
        };

        /**
         * Destroys the Swiper instance.
         */
        const destroySwiper = () => {
          if (swiperInstance.value && !swiperInstance.value.destroyed) {
            swiperInstance.value.destroy(true, true);
            swiperInstance.value = null;
          }
        };

        const fadeInCarousel = () => {
          const carouselElement = container.querySelector('[data-product-hero-carousel]');
          if (carouselElement) {
            gsap.to(carouselElement, ANIMATION_CONFIGS.FADE_IN);
          }
        };

        /**
         * Handles colorway selection and updates the URL with the correct query params.
         * @param {string} handle - The product handle to navigate to.
         * @param {string} edition - The currently selected edition tab ("all", "sale", or edition name).
         */
        const handleSetCurrentColorway = (handle, edition) => {
          if (!handle) return;

          const params = new URLSearchParams(window.location.search);

          params.delete(EDITION_PARAM);

          if (edition && edition !== EDITIONS.ALL) {
            params.set(EDITION_PARAM, edition);
          }
          // If edition is "all", no params are set

          const newUrl = new URL(
            `${window.location.origin}/products/${handle}${params.toString() ? `?${params.toString()}` : ''}`,
          );
          window.location.href = newUrl.toString();
        };

        /**
         * Initializes the default colorway name
         */
        const setDefaultColorwayName = () => {
          const defaultName = container
            .querySelector('[data-product-color-selector]')
            ?.getAttribute('data-default-colorway-name');
          if (defaultName) {
            colorwayName.value = defaultName;
          }
        };

        /**
         * Handles edition tab click events
         * @param {string} edition - The selected edition
         */
        const setEditionTab = (edition) => {
          selectedEdition.value = edition;
        };

        const setInitialEdition = () => {
          const params = new URLSearchParams(window.location.search);
          const saleParam = params.get(EDITIONS.SALE);
          if (saleParam === 'true') {
            selectedEdition.value = EDITIONS.SALE;
            isSaleProduct.value = true;
          } else if (params.has(EDITION_PARAM)) {
            selectedEdition.value = params.get(EDITION_PARAM);
          } else {
            selectedEdition.value = EDITIONS.ALL;
          }
        };

        /**
         * Updates the colorway name when hovering over a swatch
         * @param {string} name - The colorway name to display
         */
        const setColorwayName = (name) => {
          colorwayName.value = name;
        };

        onMounted(() => {
          initSwiper();
          setInitialEdition();
          setDefaultColorwayName();
        });

        onUnmounted(() => {
          destroySwiper();
        });

        return {
          colorwayName,
          currentSlideIndex,
          handleSetCurrentColorway,
          isFinalSaleTooltipOpen,
          isSaleProduct,
          selectedEdition,
          setColorwayName,
          setDefaultColorwayName,
          setEditionTab,
          swiperIsBeginning,
          swiperIsEnd,
        };
      },
    }).mount(container);
  }
}

new ProductHeroSection();
